import { useEffect } from 'react';
import VisibilityIcon from '@mui/icons-material/Visibility';
import HourglassEmpty from '@mui/icons-material/HourglassEmpty';

import type { Row as RowType } from '@tanstack/react-table';
import type { GetAllHangingProtocolsQuery } from 'generated/graphql';
import type { HangingProtocol } from '../../../HangingProtocol/useHangingProtocol';
import { ActionButton } from './ActionButton';

type HangingProtocolItem = GetAllHangingProtocolsQuery['hangingProtocols']['items'][number];

type PreviewCellProps = {
  row: RowType<HangingProtocolItem>;
  hangingProtocol: Nullable<HangingProtocol>;
  /**
   * Asks the widget to preview this protocol. Named as a request rather than an action because the
   * widget may first put up a confirmation -- unsaved clinical changes are discarded by a preview, so
   * the decision of whether the preview actually runs is not this cell's to make.
   */
  requestPreview: (smid: string) => unknown;
  setHighlightRow: (arg1: number) => unknown;
  isLoadingPreview: boolean;
};

export const PreviewCell = ({
  row,
  hangingProtocol,
  requestPreview,
  setHighlightRow,
  isLoadingPreview,
}: PreviewCellProps): React.ReactElement => {
  useEffect(() => {
    if (row.original.smid != null && row.original.smid === hangingProtocol?.smid) {
      setHighlightRow(row.index);
    }
  }, [hangingProtocol?.smid, row.index, row.original.smid, setHighlightRow]);

  return (
    <ActionButton
      data-testid={`preview-button-${row.index + 1}`}
      onClick={() => {
        if (row.original.smid != null) {
          requestPreview(row.original.smid);
        }
      }}
      // Originally we were showing a <CircularProgress /> (animated progress spinner) for the
      // loading preview state, but because of the custom CSS defined in the parent component
      // <Button>, the icon was not rendering properly and it was a blank.
      icon={isLoadingPreview ? <HourglassEmpty /> : <VisibilityIcon />}
      disabled={isLoadingPreview}
    >
      Preview
    </ActionButton>
  );
};
